// =============================================================================
// Vehicle-transfer admin types -- mirror backend Pydantic schemas.
// =============================================================================
// Source of truth:
//   backend/app/schemas/vehicle_transfer.py       (VehicleTransferRead,
//                                                  VehicleTransferInitiateResponse)
//   backend/app/api/v1/admin_vehicle_transfers.py (AdminParkedTransferRow,
//                                                  AdminParkedTransferListResponse)
//
// NOTE ON `secret_code`. Only `AdminTransferApproveResponse` carries it,
// mirroring the backend's type split: the queue row extends the
// code-free read model, so "the queue cannot render a code" is a
// property of these types rather than of a code path someone has to
// remember. Nothing here is ever persisted -- see VehicleTransfersPage.
// =============================================================================

import type { ApiError } from "@/types/auth";

/** Mirrors `VehicleTransferRead` -- deliberately has no `secret_code`. */
export interface VehicleTransferRead {
  id: string;
  vehicle_id: string;
  old_driver_id: string;
  new_driver_id: string;
  initiator_role: string;
  status: string;
  reason: string | null;
  code_expires_at: string | null;
  effective_at: string | null;
  created_at: string;
}

/**
 * Mirrors `AdminParkedTransferRow`: a parked transfer plus the display
 * context an operator needs, so reviewing a row costs no extra fetches.
 */
export interface AdminParkedTransferRow extends VehicleTransferRead {
  vehicle_registration_number: string | null;
  old_driver_name: string | null;
  new_driver_name: string | null;
  new_driver_phone: string | null;
  initiator_user_id: string;
  /**
   * Resolved live by the backend on every list call, not stored: the
   * blocker can clear itself when the incoming driver's other vehicle
   * moves on, and a stale flag would send an admin chasing a problem
   * that no longer exists.
   */
  new_driver_has_active_vehicle: boolean;
  /**
   * FSD p94's admin-approval conditions, as stable reason codes, recorded
   * at initiate. NULL for rows parked before migration 0068, when the
   * active-vehicle blocker was the only trigger that existed and the
   * reason could safely be assumed.
   *
   * An array because p94's conditions are not mutually exclusive: a
   * suspended driver taking a truck with lapsed insurance trips two.
   */
  admin_approval_reasons: string[] | null;
}

export interface AdminParkedTransferListResponse {
  items: AdminParkedTransferRow[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Mirrors `VehicleTransferInitiateResponse`, which the approve route
 * reuses. `secret_code` is the plaintext handover code and this is the
 * ONLY place in the admin API it is ever emitted (decision D-10). No GET
 * returns it; the backend stores a hash only.
 */
export interface AdminTransferApproveResponse extends VehicleTransferRead {
  secret_code: string | null;
}

export interface AdminTransferApprovePayload {
  notes?: string;
}

export interface AdminTransferRejectPayload {
  /** Mandatory server-side (`min_length=1`); the UI blocks the empty case. */
  notes: string;
}

/**
 * Pull `blocking_vehicle_id` out of a 409 problem-detail body.
 *
 * `BusinessRuleViolationError(extra=...)` is flattened onto the RFC 7807
 * payload by `to_problem_details()`, so the id sits at the TOP level of
 * the body next to `code` -- it is simply not in our hand-maintained
 * `ProblemDetail` interface, which only models the fields every problem
 * response shares. Reading it through an index rather than widening
 * `ProblemDetail` keeps the one-off shape local to the one surface that
 * needs it.
 */
export function blockingVehicleIdOf(error: ApiError): string | null {
  const raw = (error.problem as unknown as Record<string, unknown>)[
    "blocking_vehicle_id"
  ];
  return typeof raw === "string" ? raw : null;
}
